import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import {
  Globe, Palette, Video, TrendingUp, Server, Plus, Trash2, Save, Edit2, 
  GripVertical, Eye, EyeOff, Clock, Users, Building, Phone, Mail, MapPin,
  Calendar, ChevronDown, ChevronUp, X, Check, Loader2, Euro
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icon mapping
const ICON_MAP = {
  Globe, Palette, Video, TrendingUp, Server, Users, Building, Clock, Calendar,
  Star: Globe, Award: Palette, Book: Globe, Share2: TrendingUp, FileText: Globe,
  Play: Video, Camera: Video, Wand2: Video, Film: Video, Search: TrendingUp,
  Target: TrendingUp, Mail: Mail, Monitor: Globe, Zap: Server, Code: Globe,
  Smartphone: Globe, ShoppingBag: Globe, Shield: Server, Sparkles: Palette
};

const getIcon = (iconName) => ICON_MAP[iconName] || Globe;

const WebsiteContentPage = () => {
  const { user, token } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('services');
  
  // Data states
  const [services, setServices] = useState([]);
  const [solutions, setSolutions] = useState([]);
  const [team, setTeam] = useState([]);
  const [companyInfo, setCompanyInfo] = useState(null);
  
  // Edit states
  const [editingService, setEditingService] = useState(null);
  const [editingSolution, setEditingSolution] = useState(null);
  const [editingMember, setEditingMember] = useState(null);
  const [expandedService, setExpandedService] = useState(null);

  // Fetch all data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/website/public-data`);
      const data = await response.json();
      
      setServices(data.services || []);
      setSolutions(data.solutions || []);
      setTeam(data.team || []);
      setCompanyInfo(data.company_info || {});
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Save service
  const saveService = async (service) => {
    setSaving(true);
    try {
      const method = service.id ? 'PUT' : 'POST';
      const url = service.id 
        ? `${API_URL}/api/website/services/${service.id}`
        : `${API_URL}/api/website/services`;
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(service)
      });
      
      if (!response.ok) throw new Error('Failed to save');
      
      toast.success('Service enregistré !');
      setEditingService(null);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  // Save solution
  const saveSolution = async (solution) => {
    setSaving(true);
    try {
      const method = solution.id ? 'PUT' : 'POST';
      const url = solution.id 
        ? `${API_URL}/api/website/solutions/${solution.id}`
        : `${API_URL}/api/website/solutions`;
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(solution)
      });
      
      if (!response.ok) throw new Error('Failed to save');
      
      toast.success('Solution enregistrée !');
      setEditingSolution(null);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  // Save team member
  const saveTeamMember = async (member) => {
    setSaving(true);
    try {
      const method = member.id ? 'PUT' : 'POST';
      const url = member.id 
        ? `${API_URL}/api/website/team/${member.id}`
        : `${API_URL}/api/website/team`;
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(member)
      });
      
      if (!response.ok) throw new Error('Failed to save');
      
      toast.success('Membre enregistré !');
      setEditingMember(null);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  // Save company info
  const saveCompanyInfo = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/website/company-info`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(companyInfo)
      });
      
      if (!response.ok) throw new Error('Failed to save');
      
      toast.success('Informations de l\'entreprise enregistrées !');
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  // Delete solution
  const deleteSolution = async (solutionId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette solution ?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/website/solutions/${solutionId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to delete');
      
      toast.success('Solution supprimée');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Delete team member
  const deleteTeamMember = async (memberId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce membre ?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/website/team/${memberId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to delete');
      
      toast.success('Membre supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Render Service Card
  const ServiceCard = ({ service }) => {
    const Icon = getIcon(service.icon);
    const serviceSolutions = solutions.filter(s => s.service_slug === service.slug);
    const isExpanded = expandedService === service.id;
    
    return (
      <Card className="mb-4">
        <CardHeader 
          className="cursor-pointer hover:bg-slate-50 transition-colors"
          onClick={() => setExpandedService(isExpanded ? null : service.id)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div 
                className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: `${service.color}20` }}
              >
                <Icon className="w-5 h-5" style={{ color: service.color }} />
              </div>
              <div>
                <CardTitle className="text-lg">{service.name}</CardTitle>
                <CardDescription>{service.description}</CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant={service.is_active ? "default" : "secondary"}>
                {service.is_active ? 'Actif' : 'Inactif'}
              </Badge>
              <Badge variant="outline">{serviceSolutions.length} solutions</Badge>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={(e) => { e.stopPropagation(); setEditingService(service); }}
              >
                <Edit2 className="w-4 h-4" />
              </Button>
              {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </div>
          </div>
        </CardHeader>
        
        {isExpanded && (
          <CardContent className="border-t pt-4">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-semibold">Solutions / Offres</h4>
              <Button 
                size="sm" 
                onClick={() => setEditingSolution({ service_slug: service.slug, is_active: true, features: [], options: [] })}
              >
                <Plus className="w-4 h-4 mr-2" /> Ajouter
              </Button>
            </div>
            
            <div className="space-y-3">
              {serviceSolutions.map(solution => (
                <div 
                  key={solution.id}
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <GripVertical className="w-4 h-4 text-slate-400" />
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{solution.name}</span>
                        {solution.badge && (
                          <Badge variant="secondary" className="text-xs">{solution.badge}</Badge>
                        )}
                        {solution.is_subscription && (
                          <Badge variant="outline" className="text-xs">Abonnement</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <Euro className="w-3 h-3" />
                        <span>{solution.price_display}</span>
                        {solution.delivery_time && (
                          <>
                            <span className="mx-1">•</span>
                            <Clock className="w-3 h-3" />
                            <span>{solution.delivery_time}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {solution.is_featured && <Badge className="bg-amber-500">Populaire</Badge>}
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setEditingSolution(solution)}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      className="text-red-500 hover:text-red-700"
                      onClick={() => deleteSolution(solution.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
              
              {serviceSolutions.length === 0 && (
                <p className="text-center text-slate-500 py-4">
                  Aucune solution pour ce service. Cliquez sur "Ajouter" pour en créer une.
                </p>
              )}
            </div>
          </CardContent>
        )}
      </Card>
    );
  };

  // Solution Edit Modal
  const SolutionEditModal = ({ solution, onSave, onClose }) => {
    const [form, setForm] = useState({
      ...solution,
      features: solution.features || [],
      options: solution.options || [],
      included_items: solution.included_items || [],
      not_included_items: solution.not_included_items || []
    });
    const [newFeature, setNewFeature] = useState('');
    const [newOption, setNewOption] = useState({ name: '', price: 0, description: '' });

    const addFeature = () => {
      if (newFeature.trim()) {
        setForm({ ...form, features: [...form.features, newFeature.trim()] });
        setNewFeature('');
      }
    };

    const removeFeature = (index) => {
      setForm({ ...form, features: form.features.filter((_, i) => i !== index) });
    };

    const addOption = () => {
      if (newOption.name.trim()) {
        setForm({ ...form, options: [...form.options, { ...newOption }] });
        setNewOption({ name: '', price: 0, description: '' });
      }
    };

    const removeOption = (index) => {
      setForm({ ...form, options: form.options.filter((_, i) => i !== index) });
    };

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between">
            <h3 className="text-lg font-semibold">
              {solution.id ? 'Modifier la solution' : 'Nouvelle solution'}
            </h3>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>
          
          <div className="p-6 space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Nom</Label>
                <Input 
                  value={form.name || ''} 
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Ex: Site Vitrine"
                />
              </div>
              <div>
                <Label>Slug (URL)</Label>
                <Input 
                  value={form.slug || ''} 
                  onChange={(e) => setForm({ ...form, slug: e.target.value })}
                  placeholder="ex: site-vitrine"
                />
              </div>
              <div>
                <Label>Badge (optionnel)</Label>
                <Input 
                  value={form.badge || ''} 
                  onChange={(e) => setForm({ ...form, badge: e.target.value })}
                  placeholder="Ex: Populaire, Nouveau"
                />
              </div>
            </div>
            
            {/* Description */}
            <div>
              <Label>Description courte</Label>
              <Textarea 
                value={form.description || ''} 
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Description affichée sur les cartes"
                rows={2}
              />
            </div>
            
            {/* Pricing */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Prix minimum (€ HT)</Label>
                <Input 
                  type="number"
                  value={form.price_min || 0} 
                  onChange={(e) => setForm({ ...form, price_min: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div>
                <Label>Affichage prix</Label>
                <Input 
                  value={form.price_display || ''} 
                  onChange={(e) => setForm({ ...form, price_display: e.target.value })}
                  placeholder="Ex: À partir de 1 500€ HT"
                />
              </div>
            </div>
            
            {/* Subscription */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Switch 
                  checked={form.is_subscription || false}
                  onCheckedChange={(checked) => setForm({ ...form, is_subscription: checked })}
                />
                <Label>Abonnement récurrent</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch 
                  checked={form.is_featured || false}
                  onCheckedChange={(checked) => setForm({ ...form, is_featured: checked })}
                />
                <Label>Mettre en avant (Populaire)</Label>
              </div>
            </div>
            
            {/* Delivery Time */}
            <div>
              <Label>Délai de livraison</Label>
              <Input 
                value={form.delivery_time || ''} 
                onChange={(e) => setForm({ ...form, delivery_time: e.target.value })}
                placeholder="Ex: 2-4 semaines"
              />
            </div>
            
            {/* Features */}
            <div>
              <Label className="mb-2 block">Fonctionnalités incluses</Label>
              <div className="space-y-2 mb-2">
                {form.features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-2 bg-slate-50 p-2 rounded">
                    <Check className="w-4 h-4 text-green-500" />
                    <span className="flex-1">{feature}</span>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => removeFeature(index)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <Input 
                  value={newFeature}
                  onChange={(e) => setNewFeature(e.target.value)}
                  placeholder="Nouvelle fonctionnalité"
                  onKeyPress={(e) => e.key === 'Enter' && addFeature()}
                />
                <Button onClick={addFeature} size="sm">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            {/* Options */}
            <div>
              <Label className="mb-2 block">Options additionnelles (payantes)</Label>
              <div className="space-y-2 mb-2">
                {form.options.map((option, index) => (
                  <div key={index} className="flex items-center gap-2 bg-amber-50 p-2 rounded">
                    <Plus className="w-4 h-4 text-amber-500" />
                    <span className="flex-1">
                      {option.name} - <strong>{option.price}€</strong>
                      {option.description && <span className="text-slate-500 text-sm ml-2">({option.description})</span>}
                    </span>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => removeOption(index)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-3 gap-2">
                <Input 
                  value={newOption.name}
                  onChange={(e) => setNewOption({ ...newOption, name: e.target.value })}
                  placeholder="Nom option"
                />
                <Input 
                  type="number"
                  value={newOption.price}
                  onChange={(e) => setNewOption({ ...newOption, price: parseInt(e.target.value) || 0 })}
                  placeholder="Prix €"
                />
                <Button onClick={addOption} size="sm">
                  <Plus className="w-4 h-4 mr-1" /> Ajouter
                </Button>
              </div>
            </div>
          </div>
          
          <div className="sticky bottom-0 bg-white border-t p-4 flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>Annuler</Button>
            <Button onClick={() => onSave(form)} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Enregistrer
            </Button>
          </div>
        </div>
      </div>
    );
  };

  // Team Member Edit Modal
  const TeamMemberEditModal = ({ member, onSave, onClose }) => {
    const [form, setForm] = useState({
      ...member,
      specialties: member.specialties || []
    });
    const [newSpecialty, setNewSpecialty] = useState('');

    const addSpecialty = () => {
      if (newSpecialty.trim()) {
        setForm({ ...form, specialties: [...form.specialties, newSpecialty.trim()] });
        setNewSpecialty('');
      }
    };

    const removeSpecialty = (index) => {
      setForm({ ...form, specialties: form.specialties.filter((_, i) => i !== index) });
    };

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between">
            <h3 className="text-lg font-semibold">
              {member.id ? 'Modifier le membre' : 'Nouveau membre'}
            </h3>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>
          
          <div className="p-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Prénom</Label>
                <Input 
                  value={form.first_name || ''} 
                  onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                />
              </div>
              <div>
                <Label>Nom</Label>
                <Input 
                  value={form.last_name || ''} 
                  onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                />
              </div>
            </div>
            
            <div>
              <Label>Poste / Rôle</Label>
              <Input 
                value={form.role || ''} 
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                placeholder="Ex: Directeur Artistique"
              />
            </div>
            
            <div>
              <Label>Bio</Label>
              <Textarea 
                value={form.bio || ''} 
                onChange={(e) => setForm({ ...form, bio: e.target.value })}
                rows={3}
              />
            </div>
            
            <div>
              <Label>URL Avatar</Label>
              <Input 
                value={form.avatar_url || ''} 
                onChange={(e) => setForm({ ...form, avatar_url: e.target.value })}
                placeholder="https://..."
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Email</Label>
                <Input 
                  type="email"
                  value={form.email || ''} 
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </div>
              <div>
                <Label>LinkedIn</Label>
                <Input 
                  value={form.linkedin || ''} 
                  onChange={(e) => setForm({ ...form, linkedin: e.target.value })}
                />
              </div>
            </div>
            
            <div>
              <Label className="mb-2 block">Spécialités</Label>
              <div className="flex flex-wrap gap-2 mb-2">
                {form.specialties.map((spec, index) => (
                  <Badge 
                    key={index} 
                    variant="secondary"
                    className="cursor-pointer"
                    onClick={() => removeSpecialty(index)}
                  >
                    {spec} <X className="w-3 h-3 ml-1" />
                  </Badge>
                ))}
              </div>
              <div className="flex gap-2">
                <Input 
                  value={newSpecialty}
                  onChange={(e) => setNewSpecialty(e.target.value)}
                  placeholder="Nouvelle spécialité"
                  onKeyPress={(e) => e.key === 'Enter' && addSpecialty()}
                />
                <Button onClick={addSpecialty} size="sm">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Switch 
                  checked={form.is_active !== false}
                  onCheckedChange={(checked) => setForm({ ...form, is_active: checked })}
                />
                <Label>Actif</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch 
                  checked={form.is_featured || false}
                  onCheckedChange={(checked) => setForm({ ...form, is_featured: checked })}
                />
                <Label>Mettre en avant</Label>
              </div>
            </div>
          </div>
          
          <div className="sticky bottom-0 bg-white border-t p-4 flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>Annuler</Button>
            <Button onClick={() => onSave(form)} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Enregistrer
            </Button>
          </div>
        </div>
      </div>
    );
  };

  if (user?.role !== 'agency') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="p-8 text-center">
          <h2 className="text-xl font-bold text-red-500">Accès refusé</h2>
          <p className="text-slate-600 mt-2">Cette page est réservée aux administrateurs.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar
        collapsed={collapsed}
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />

      <main className={cn(
        "transition-all duration-300 p-4 md:p-8 pt-24",
        collapsed ? "md:ml-20" : "md:ml-64"
      )}>
        {/* Page Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-900">Gestion du Contenu Site Web</h1>
          <p className="text-slate-500">Gérez les services, tarifs, équipe et informations de l'entreprise</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
          </div>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-6">
              <TabsTrigger value="services" className="gap-2">
                <Globe className="w-4 h-4" />
                Services & Tarifs
              </TabsTrigger>
              <TabsTrigger value="team" className="gap-2">
                <Users className="w-4 h-4" />
                Équipe
              </TabsTrigger>
              <TabsTrigger value="company" className="gap-2">
                <Building className="w-4 h-4" />
                Entreprise
              </TabsTrigger>
            </TabsList>

            {/* Services Tab */}
            <TabsContent value="services">
              <div className="mb-4 flex justify-between items-center">
                <p className="text-sm text-slate-500">
                  Cliquez sur un service pour voir et gérer ses solutions/offres
                </p>
              </div>
              
              {services.map(service => (
                <ServiceCard key={service.id} service={service} />
              ))}
            </TabsContent>

            {/* Team Tab */}
            <TabsContent value="team">
              <div className="mb-4 flex justify-between items-center">
                <p className="text-sm text-slate-500">
                  Gérez les membres de l'équipe affichés sur le site
                </p>
                <Button onClick={() => setEditingMember({ is_active: true, specialties: [] })}>
                  <Plus className="w-4 h-4 mr-2" /> Ajouter un membre
                </Button>
              </div>
              
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {team.map(member => (
                  <Card key={member.id}>
                    <CardContent className="pt-6">
                      <div className="flex items-start gap-4">
                        <div className="w-16 h-16 rounded-full bg-slate-200 flex items-center justify-center overflow-hidden">
                          {member.avatar_url ? (
                            <img src={member.avatar_url} alt={member.first_name} className="w-full h-full object-cover" />
                          ) : (
                            <Users className="w-8 h-8 text-slate-400" />
                          )}
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold">{member.first_name} {member.last_name}</h3>
                          <p className="text-sm text-slate-500">{member.role}</p>
                          <div className="flex flex-wrap gap-1 mt-2">
                            {member.specialties?.slice(0, 3).map((spec, i) => (
                              <Badge key={i} variant="secondary" className="text-xs">{spec}</Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
                        <Button variant="ghost" size="sm" onClick={() => setEditingMember(member)}>
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-red-500"
                          onClick={() => deleteTeamMember(member.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>

            {/* Company Tab */}
            <TabsContent value="company">
              <div className="space-y-6">
                {/* Special Message Alert */}
                <Card className="border-amber-200 bg-amber-50">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Clock className="w-5 h-5 text-amber-600" />
                        Message Spécial / Fermeture Exceptionnelle
                      </CardTitle>
                      <Switch 
                        checked={companyInfo?.special_message_active || false}
                        onCheckedChange={(checked) => setCompanyInfo({ ...companyInfo, special_message_active: checked })}
                      />
                    </div>
                    <CardDescription>
                      Ce message sera affiché dans le header du site et de l'espace client
                    </CardDescription>
                  </CardHeader>
                  {companyInfo?.special_message_active && (
                    <CardContent>
                      <Textarea 
                        value={companyInfo?.special_message || ''} 
                        onChange={(e) => setCompanyInfo({ ...companyInfo, special_message: e.target.value })}
                        placeholder="Ex: Fermé exceptionnellement le 25 décembre. En congés du 23 décembre au 6 janvier. Joyeuses fêtes !"
                        rows={2}
                        className="bg-white"
                      />
                    </CardContent>
                  )}
                </Card>

                {/* Logos */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Globe className="w-5 h-5" />
                      Logos de l'agence
                    </CardTitle>
                    <CardDescription>
                      Les logos apparaissent dans le header et le footer
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-6 md:grid-cols-2">
                      <div>
                        <Label className="mb-2 block">Logo principal (mode standard)</Label>
                        <div className="flex items-center gap-4">
                          <div className="w-20 h-20 rounded-full bg-slate-100 border-2 border-slate-200 flex items-center justify-center overflow-hidden">
                            {companyInfo?.logo_url ? (
                              <img src={companyInfo.logo_url} alt="Logo" className="w-full h-full object-cover" />
                            ) : (
                              <span className="text-slate-400 text-xs text-center">Aucun logo</span>
                            )}
                          </div>
                          <div className="flex-1">
                            <Input 
                              value={companyInfo?.logo_url || ''} 
                              onChange={(e) => setCompanyInfo({ ...companyInfo, logo_url: e.target.value })}
                              placeholder="URL de l'image (PNG, JPG)"
                            />
                          </div>
                        </div>
                      </div>
                      <div>
                        <Label className="mb-2 block">Logo mode COPILOTE</Label>
                        <div className="flex items-center gap-4">
                          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-violet-600 to-indigo-600 border-2 border-violet-400 flex items-center justify-center overflow-hidden">
                            {companyInfo?.logo_copilote_url ? (
                              <img src={companyInfo.logo_copilote_url} alt="Logo Copilote" className="w-full h-full object-cover" />
                            ) : (
                              <span className="text-white text-2xl">🚀</span>
                            )}
                          </div>
                          <div className="flex-1">
                            <Input 
                              value={companyInfo?.logo_copilote_url || ''} 
                              onChange={(e) => setCompanyInfo({ ...companyInfo, logo_copilote_url: e.target.value })}
                              placeholder="URL de l'image (PNG, JPG)"
                            />
                            <p className="text-xs text-slate-500 mt-1">Par défaut : 🚀 (fusée)</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Basic Info */}
                <Card>
                  <CardHeader>
                    <CardTitle>Informations générales</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid gap-4 md:grid-cols-2">
                      <div>
                        <Label>Nom de l'entreprise</Label>
                        <Input 
                          value={companyInfo?.name || ''} 
                          onChange={(e) => setCompanyInfo({ ...companyInfo, name: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label>Slogan</Label>
                        <Input 
                          value={companyInfo?.tagline || ''} 
                          onChange={(e) => setCompanyInfo({ ...companyInfo, tagline: e.target.value })}
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label>Description</Label>
                      <Textarea 
                        value={companyInfo?.description || ''} 
                        onChange={(e) => setCompanyInfo({ ...companyInfo, description: e.target.value })}
                        rows={3}
                      />
                    </div>
                    
                    <div className="grid gap-4 md:grid-cols-3">
                      <div className="md:col-span-2">
                        <Label>Adresse</Label>
                        <Input 
                          value={companyInfo?.address || ''} 
                          onChange={(e) => setCompanyInfo({ ...companyInfo, address: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label>Code postal</Label>
                        <Input 
                          value={companyInfo?.postal_code || ''} 
                          onChange={(e) => setCompanyInfo({ ...companyInfo, postal_code: e.target.value })}
                        />
                      </div>
                    </div>
                    
                    <div className="grid gap-4 md:grid-cols-3">
                      <div>
                        <Label>Ville</Label>
                        <Input 
                          value={companyInfo?.city || ''} 
                          onChange={(e) => setCompanyInfo({ ...companyInfo, city: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label>Téléphone</Label>
                        <Input 
                          value={companyInfo?.phone || ''} 
                          onChange={(e) => setCompanyInfo({ ...companyInfo, phone: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label>Email de contact</Label>
                        <Input 
                          type="email"
                          value={companyInfo?.email || ''} 
                          onChange={(e) => setCompanyInfo({ ...companyInfo, email: e.target.value })}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Social Links */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Mail className="w-5 h-5" />
                      Réseaux Sociaux
                    </CardTitle>
                    <CardDescription>
                      Liens affichés dans le footer du site
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-4 md:grid-cols-2">
                      {[
                        { key: 'facebook', label: 'Facebook', placeholder: 'https://facebook.com/...' },
                        { key: 'instagram', label: 'Instagram', placeholder: 'https://instagram.com/...' },
                        { key: 'linkedin', label: 'LinkedIn', placeholder: 'https://linkedin.com/company/...' },
                        { key: 'twitter', label: 'X (Twitter)', placeholder: 'https://x.com/...' },
                        { key: 'youtube', label: 'YouTube', placeholder: 'https://youtube.com/@...' },
                        { key: 'tiktok', label: 'TikTok', placeholder: 'https://tiktok.com/@...' },
                        { key: 'pinterest', label: 'Pinterest', placeholder: 'https://pinterest.com/...' }
                      ].map(social => (
                        <div key={social.key}>
                          <Label>{social.label}</Label>
                          <Input 
                            value={companyInfo?.social_links?.[social.key] || ''} 
                            onChange={(e) => setCompanyInfo({ 
                              ...companyInfo, 
                              social_links: { 
                                ...companyInfo?.social_links, 
                                [social.key]: e.target.value 
                              }
                            })}
                            placeholder={social.placeholder}
                          />
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Opening Hours */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Clock className="w-5 h-5" />
                      Horaires d'ouverture
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-3">
                      {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => {
                        const dayNames = {
                          monday: 'Lundi', tuesday: 'Mardi', wednesday: 'Mercredi',
                          thursday: 'Jeudi', friday: 'Vendredi', saturday: 'Samedi', sunday: 'Dimanche'
                        };
                        const hours = companyInfo?.opening_hours?.[day] || { open: '09:00', close: '18:00', is_open: false };
                        
                        return (
                          <div key={day} className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
                            <span className="w-24 font-medium">{dayNames[day]}</span>
                            <Switch 
                              checked={hours.is_open}
                              onCheckedChange={(checked) => setCompanyInfo({
                                ...companyInfo,
                                opening_hours: {
                                  ...companyInfo.opening_hours,
                                  [day]: { ...hours, is_open: checked }
                                }
                              })}
                            />
                            {hours.is_open ? (
                              <div className="flex items-center gap-2">
                                <Input 
                                  type="time"
                                  value={hours.open}
                                  onChange={(e) => setCompanyInfo({
                                    ...companyInfo,
                                    opening_hours: {
                                      ...companyInfo.opening_hours,
                                      [day]: { ...hours, open: e.target.value }
                                    }
                                  })}
                                  className="w-32"
                                />
                                <span>à</span>
                                <Input 
                                  type="time"
                                  value={hours.close}
                                  onChange={(e) => setCompanyInfo({
                                    ...companyInfo,
                                    opening_hours: {
                                      ...companyInfo.opening_hours,
                                      [day]: { ...hours, close: e.target.value }
                                    }
                                  })}
                                  className="w-32"
                                />
                              </div>
                            ) : (
                              <span className="text-slate-500">Fermé</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>

                {/* Site Info */}
                <Card>
                  <CardHeader>
                    <CardTitle>Informations du site</CardTitle>
                    <CardDescription>
                      Affichées dans le footer
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-4 md:grid-cols-2">
                      <div>
                        <Label>Version du site</Label>
                        <Input 
                          value={companyInfo?.site_version || ''} 
                          onChange={(e) => setCompanyInfo({ ...companyInfo, site_version: e.target.value })}
                          placeholder="Ex: 1.0.0"
                        />
                      </div>
                      <div>
                        <Label>Dernière mise à jour</Label>
                        <Input 
                          value={companyInfo?.last_updated ? new Date(companyInfo.last_updated).toLocaleDateString('fr-FR') : ''} 
                          disabled
                          className="bg-slate-100"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Save Button */}
                <div className="flex justify-end pt-4">
                  <Button onClick={saveCompanyInfo} disabled={saving} size="lg">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                    Enregistrer toutes les modifications
                  </Button>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        )}
      </main>

      {/* Modals */}
      {editingSolution && (
        <SolutionEditModal 
          solution={editingSolution}
          onSave={saveSolution}
          onClose={() => setEditingSolution(null)}
        />
      )}
      
      {editingMember && (
        <TeamMemberEditModal 
          member={editingMember}
          onSave={saveTeamMember}
          onClose={() => setEditingMember(null)}
        />
      )}
    </div>
  );
};

export default WebsiteContentPage;
